import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import TemplateSelection from './TemplateSelectionModal';
import FetchReport from './getReport';
import { Icon } from '@ohif/ui';
import { useV2Theme } from '../../../../../platform/app/src/providers/theme/ThemeProvider';
import { ReactComponent as LightThemeLogo } from '../../../../../platform/ui/src/assets/svgs/symbian-logo.svg';
import AISuggestionBox from './AISuggestionBox';
import { VoiceDictationControls, NavigationButton } from './components';
import {
    useVoiceRecognition,
    useFormNavigation,
    useFocusManagement,
    useVoiceInputHandler,
    useVoiceRecognitionMonitor,
    useVoiceDictationInterruption
} from './hooks';

const ReportControls = ({
    onClose,
    onSaveReport,
    template,
    templateUrl,
    templateContent,
    fullWidth = false,
    isOpen
}) => {
    // CSS class constants
    const TAB_BUTTON_BASE_CLASSES = "flex items-center gap-2 px-4 py-2 rounded-xl text-[14px] font-semibold transition-all duration-150 border";
    const TAB_BUTTON_ACTIVE_CLASSES = "shadow-sm";
    const TAB_BUTTON_INACTIVE_CLASSES = "hover:opacity-90 active:scale-[0.98]";
    const SAVE_BUTTON_ENABLED_CLASSES = "shadow-md hover:shadow-lg hover:opacity-95 active:scale-[0.98]";
    const SAVE_BUTTON_DISABLED_CLASSES = "cursor-not-allowed opacity-50";

    // Component state
    const [activeTab, setActiveTab] = useState<'create' | 'see' | 'dictation'>('see');
    const [hasReport, setHasReport] = useState<boolean | null>(null);
    const [isFetchingReport, setIsFetchingReport] = useState(false);
    const [isTemplateSelected, setIsTemplateSelected] = useState(false);
    const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isFirstLoadDone, setIsFirstLoadDone] = useState(false);
    const [hasEditableFields, setHasEditableFields] = useState(false);

    // Component refs
    const templateRef = useRef<any>(null);
    const fetchReportRef = useRef<any>(null);
    const isFirstLoad = useRef(true);

    // Mock suggestions
    const mockSuggestions = [
        "Expand on the patient's symptoms.",
        "Include relevant medical history.",
        "Mention any previous treatments or diagnoses.",
    ];

    // Custom hooks
    const voiceRecognition = useVoiceRecognition({
        maxRestartAttempts: 3,
        timeoutThreshold: 10000,
        navigationDebounceMs: 500
    });

    const formNavigation = useFormNavigation();

    const { isFocusedInsideReport, resetFocusState } = useFocusManagement(
        formNavigation.lastFocusedRef,
        formNavigation.setLastFocusedElement,
        formNavigation.updateCurrentInputIndex
    );

    const { handleVoiceInput } = useVoiceInputHandler(
        formNavigation.lastFocusedRef,
        voiceRecognition.lastInterimTextRef,
        () => {
            if (!voiceRecognition.canNavigate()) return false;

            const success = formNavigation.focusNextInput();

            const timeSinceLastInput = Date.now() - voiceRecognition.getVoiceState().lastVoiceInputTime;
            const isListening = voiceRecognition.voiceRecordingRef.current?.isListening?.();

            if (success && timeSinceLastInput > 5000 && !isListening) {
                setTimeout(() => {
                    voiceRecognition.restartVoiceRecognition('navigation');
                }, 300);
            }

            return success;
        },
        voiceRecognition.isNavigationCommand,
        voiceRecognition.updateVoiceInputTime,
        setHasChanges,
        voiceRecognition.commandCacheRef,
        formNavigation.focusFirstInput,
        formNavigation.focusLastInput
    );

    useVoiceRecognitionMonitor(
        voiceRecognition.voiceRecordingRef,
        formNavigation.lastFocusedRef,
        voiceRecognition.shouldRestartVoice,
        voiceRecognition.restartVoiceRecognition
    );

    useVoiceDictationInterruption(
        voiceRecognition.voiceRecordingRef,
        voiceRecognition.resetVoiceState,
        {
            isLoading: isFetchingReport,
            isSaving: saving,
            templateChanged: activeTab === 'create' && !isTemplateLoaded,
            tabChanged: activeTab
        }
    );

    // ⭐ ADDED: detect whether report contains fields
    const detectEditableFields = () => {
        const el = document.getElementById("report");
        if (!el) return false;

        return !!el.querySelector("input, textarea, select, [data-report-field]");
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setHasEditableFields(detectEditableFields());
        }, 400);

        return () => clearInterval(interval);
    }, [activeTab, isFetchingReport]);

    const getTabButtonClasses = (isActive: boolean) =>
        `${TAB_BUTTON_BASE_CLASSES} ${isActive ? TAB_BUTTON_ACTIVE_CLASSES : TAB_BUTTON_INACTIVE_CLASSES}`;

    const getTabButtonStyle = (isActive: boolean) => ({
        background: isActive
            ? 'linear-gradient(135deg, var(--v2-primary-500, #6366f1), var(--v2-primary-700, #4338ca))'
            : 'transparent',
        color: isActive ? '#ffffff' : 'var(--v2-text-secondary)',
        borderColor: isActive ? 'transparent' : 'var(--v2-border-subtle)',
        boxShadow: isActive ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
    });

    const getSaveButtonClasses = (isDisabled: boolean) =>
        `rounded-xl px-6 py-2.5 text-[14px] font-semibold transition-all duration-150 ${isDisabled ? SAVE_BUTTON_DISABLED_CLASSES : SAVE_BUTTON_ENABLED_CLASSES
        }`;

    const getSaveButtonStyle = (isDisabled: boolean) => ({
        background: isDisabled
            ? 'var(--v2-bg-card-inner)'
            : 'linear-gradient(135deg, var(--v2-primary-500, #6366f1), var(--v2-primary-700, #4338ca))',
        color: isDisabled ? 'var(--v2-text-muted)' : '#ffffff',
        border: isDisabled ? '1px solid var(--v2-border-subtle)' : '1px solid transparent',
        boxShadow: isDisabled ? 'none' : '0 2px 10px rgba(99,102,241,0.4)',
    });

    const isSaveDisabled = activeTab === 'see'
        ? isFetchingReport || !hasChanges || saving
        : activeTab === 'create'
            ? !isTemplateSelected || !isTemplateLoaded || saving
            : true;

    useEffect(() => {
        const handleCreateTab = (e: Event) => {
            const subTab = (e as CustomEvent).detail?.subTab;

            setActiveTab('create');

            setTimeout(() => {
                templateRef.current?.setActiveSubTab?.(subTab || 'classic');
            }, 0);
        };

        const handleStopVoiceDictation = (e: Event) => {
            const reason = (e as CustomEvent).detail?.reason || 'unknown';
            console.log('Stopping voice dictation due to:', reason);
            voiceRecognition.voiceRecordingRef.current?.stopListening();
            voiceRecognition.resetVoiceState();
        };

        window.addEventListener('openCreateReportTab', handleCreateTab);
        window.addEventListener('stopVoiceDictation', handleStopVoiceDictation);

        return () => {
            window.removeEventListener('openCreateReportTab', handleCreateTab);
            window.removeEventListener('stopVoiceDictation', handleStopVoiceDictation);
        };
    }, []);

    const handleClose = () => {
        voiceRecognition.voiceRecordingRef.current?.stopListening();
        voiceRecognition.resetVoiceState();
        setActiveTab('see');
        setHasReport(null);
        onClose();
    };

    const handleReportFetched = (hasReport: boolean) => {
        setHasReport(hasReport);

        if (isFirstLoad.current) {
            isFirstLoad.current = false;

            if (!hasReport) {
                setActiveTab('create');
                setTimeout(() => {
                    templateRef.current?.setActiveSubTab?.('radai');
                }, 0);
            }
        }
    };

    const handleSave = async () => {
        const refToUse = activeTab === 'see' ? fetchReportRef : templateRef;
        if (refToUse.current?.handleSaveReport) {
            setSaving(true);
            await refToUse.current.handleSaveReport();
            setSaving(false);
            handleClose();
        }
    };

    const handleActiveTabChange = (tab: 'create' | 'see' | 'dictation' | 'radai' | 'classic') => {
        if (tab === 'radai' || tab === 'classic') {
            setActiveTab('create');
        } else {
            setActiveTab(tab);
        }

        if (tab !== 'dictation') {
            voiceRecognition.voiceRecordingRef.current?.startListening();
        }
    };

    const { isDark } = useV2Theme();

    return (
        <div
            className={`${fullWidth ? 'w-screen h-screen' : 'w-[70vw]'} flex flex-col max-h-screen rounded-2xl overflow-hidden`}
            style={{
                background: 'var(--v2-bg-surface)',
                border: '1px solid var(--v2-border-card)',
                boxShadow: 'var(--v2-shadow-premium, 0 25px 60px -12px rgba(0,0,0,0.35))',
            }}
        >
            {/* ── Header ── matches Header.tsx design language ── */}
            <header
                className="relative flex items-center justify-between px-6 shrink-0"
                style={{
                    height: '64px',
                    background: 'var(--v2-bg-card)',
                    borderBottom: '1.5px solid var(--v2-border-card)',
                    boxShadow: 'var(--v2-shadow-header, 0 1px 6px rgba(0,0,0,0.08))',
                }}
            >
                {/* Left zone: logo only (+ slim context badge) */}
                <div className="flex items-center gap-2.5 min-w-0">
                    {/* Theme-aware Symbian logo (SVG — fully transparent) */}
                    <LightThemeLogo
                        className={`flex-shrink-0 h-8 w-auto ${isDark ? 'invert brightness-0' : ''}`}
                        aria-label="Symbian Health"
                        preserveAspectRatio="xMidYMid meet"
                    />
                </div>

                {/* Center zone: Voice dictation controls */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="pointer-events-auto">
                        {activeTab !== 'dictation' && (
                            <VoiceDictationControls
                                voiceRecordingRef={voiceRecognition.voiceRecordingRef}
                                isFocusedInsideReport={isFocusedInsideReport}
                                onVoiceInput={handleVoiceInput}
                                isContentLoaded={activeTab === 'see' ? hasReport === true : (isTemplateSelected && isTemplateLoaded)}
                                isLoading={isFetchingReport || saving}
                                isVisible={hasReport !== null || isTemplateSelected}
                            />
                        )}
                    </div>
                </div>

                {/* Right zone: Tab pills */}
                <div className="flex items-center gap-2 shrink-0">
                    {(!isFetchingReport || isFirstLoadDone) && (
                        <>
                            {/* Tab pill container — mirrors the Header.tsx right pill */}
                            <div
                                className="flex items-center gap-1.5 p-1 rounded-xl"
                                style={{
                                    background: 'var(--v2-bg-card-inner)',
                                    border: '1px solid var(--v2-border-subtle)',
                                }}
                            >
                                {[
                                    {
                                        tab: 'see',
                                        label: 'Patient Report',
                                        icon: 'file-Text'
                                    },
                                    {
                                        tab: 'create',
                                        label: 'Create Report',
                                        icon: 'creat-Icon'
                                    }
                                ].map(({ tab, label, icon }) => (
                                    <button
                                        key={tab}
                                        onClick={() => {
                                            voiceRecognition.voiceRecordingRef.current?.stopListening();
                                            voiceRecognition.resetVoiceState();
                                            resetFocusState();
                                            formNavigation.setLastFocusedElement(null);

                                            const reportContainer = document.getElementById('report');
                                            if (reportContainer) {
                                                const focusedElement = reportContainer.querySelector(':focus') as HTMLElement;
                                                if (focusedElement) {
                                                    focusedElement.blur();
                                                }
                                            }

                                            setActiveTab(tab as 'see' | 'create');
                                        }}
                                        className={getTabButtonClasses(activeTab === tab)}
                                        style={getTabButtonStyle(activeTab === tab)}
                                    >
                                        <Icon name={icon} className="w-4 h-4" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </header>

            {/* ── Body ── */}
            <section
                className="flex-1 overflow-y-auto p-6 flex gap-6"
                style={{ background: 'var(--v2-bg-surface)' }}
            >
                <div className="flex-1 relative">
                    {activeTab === 'see' ? (
                        <FetchReport
                            ref={fetchReportRef}
                            onReportFetched={handleReportFetched}
                            setIsFetching={(fetching) => {
                                setIsFetchingReport(fetching);
                                if (!fetching) {
                                    setIsFirstLoadDone(true);
                                }
                            }}
                            setHasChanges={setHasChanges}
                        />
                    ) : (
                        <TemplateSelection
                            ref={templateRef}
                            onTemplateSave={onSaveReport}
                            template={template}
                            templateUrl={templateUrl}
                            templateContent={templateContent}
                            setIsTemplateSelected={setIsTemplateSelected}
                            setIsTemplateLoaded={setIsTemplateLoaded}
                            onActiveTabChange={handleActiveTabChange}
                        />
                    )}

                    <NavigationButton
                        onNavigate={formNavigation.focusNextInput}
                        isVisible={
                            hasEditableFields &&
                            isFocusedInsideReport &&
                            !isFetchingReport &&
                            !saving &&
                            (activeTab === 'see' ? hasReport === true : isTemplateSelected && isTemplateLoaded)
                        }
                    />
                </div>

                <AISuggestionBox visible={true} suggestions={mockSuggestions} />
            </section>

            {/* ── Footer ── */}
            <footer
                className="flex items-center justify-between px-6 shrink-0"
                style={{
                    height: '60px',
                    background: 'var(--v2-bg-card)',
                    borderTop: '1.5px solid var(--v2-border-card)',
                }}
            >
                {/* Left: status indicator */}
                <div className="flex items-center gap-2">
                    {saving && (
                        <span
                            className="inline-flex items-center gap-1.5 text-[13px] font-medium"
                            style={{ color: 'var(--v2-text-muted)' }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: 'var(--v2-primary-500, #6366f1)' }}
                            />
                            {activeTab === 'see' ? 'Updating report…' : 'Saving report…'}
                        </span>
                    )}
                </div>

                {/* Right: Save button */}
                {activeTab !== 'dictation' && (
                    <button
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        className={getSaveButtonClasses(isSaveDisabled)}
                        style={getSaveButtonStyle(isSaveDisabled)}
                    >
                        {saving
                            ? activeTab === 'see'
                                ? 'Updating...'
                                : 'Saving...'
                            : activeTab === 'see'
                                ? 'Update Report'
                                : 'Save Report'}
                    </button>
                )}
            </footer>
        </div>
    );
};

ReportControls.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaveReport: PropTypes.func.isRequired,
    template: PropTypes.string,
    templateUrl: PropTypes.string,
    templateContent: PropTypes.string,
    fullWidth: PropTypes.bool,
};

export default ReportControls;
